import { BoneSide, uuidV4 } from "@project-rouge/rg-core";
import { MuscleToVector, Vectors } from "@project-rouge/rg-three";
import { Vector3 } from "three";
import { isBoneSprinklered } from "./index117.js";
import { BoneToVector } from "./index50.js";
import { hasBoneAt } from "./index176.js";
import { hasCornerCore } from "./index177.js";
const _vectorA = new Vector3();
const _vectorB = new Vector3();
const shiftWithCloseEnds = [
  "zero",
  "p2"
  /* p2 */
];
const muscleLocksDict = {
  [
    "n3"
    /* n3 */
  ]: true,
  [
    "n2"
    /* n2 */
  ]: true,
  [
    "n1"
    /* n1 */
  ]: true,
  [
    "zero"
    /* zero */
  ]: false,
  [
    "p1"
    /* p1 */
  ]: true,
  [
    "p2"
    /* p2 */
  ]: false
};
function AddMusclesToBone(bone, aggConfig) {
  const { corridorWidth, moduleDepth, cornerCore, gap } = aggConfig;
  const useGrid = !!aggConfig.gridCellWidth;
  const maxTravelDist = isBoneSprinklered(bone, aggConfig) ? aggConfig.maxTravelDistSprinklered : aggConfig.maxTravelDist;
  const maxCoreToCoreDist = aggConfig.maxTravelDistBidirectional * 2;
  const shiftOptions = {
    [
      "n3"
      /* n3 */
    ]: -corridorWidth * 0.5 - cornerCore.size[0] - gap * 0.5,
    [
      "n2"
      /* n2 */
    ]: -corridorWidth * 0.5 - cornerCore.size[1] - gap * 0.5,
    [
      "n1"
      /* n1 */
    ]: -corridorWidth * 0.5,
    [
      "zero"
      /* zero */
    ]: 0,
    [
      "p1"
      /* p1 */
    ]: corridorWidth * 0.5 - gap * 0.5,
    [
      "p2"
      /* p2 */
    ]: corridorWidth * 0.5 + moduleDepth
  };
  if (bone.loads[BoneSide.Right]) {
    const { muscle, shifts } = addMuscle({ bone, corridorWidth, useGrid, right: true });
    shiftMuscle(muscle, shifts, shiftOptions);
    setMuscleLocks(muscle, shifts);
    setMuscleTravelDist(muscle, maxTravelDist, maxCoreToCoreDist);
    setMuscleCloseEnds(muscle, shifts, useGrid);
    setSkipCores(muscle);
  }
  if (bone.loads[BoneSide.Left]) {
    const { muscle, shifts } = addMuscle({ bone, corridorWidth, useGrid, right: false });
    shiftMuscle(muscle, shifts, shiftOptions);
    setMuscleLocks(muscle, shifts);
    setMuscleTravelDist(muscle, maxTravelDist, maxCoreToCoreDist);
    setMuscleCloseEnds(muscle, shifts, useGrid);
    setSkipCores(muscle);
  }
  if (bone.muscles.every((m) => m.skipCores))
    (bone.muscles[1] || bone.muscles[0]).skipCores = false;
}
function addMuscle({ bone, corridorWidth, right, useGrid }) {
  const shifts = [
    "zero",
    "zero"
    /* zero */
  ];
  const muscle = createMuscle(bone, right ? BoneSide.Right : BoneSide.Left);
  initMusclePoints({ muscle, corridorWidth, right });
  bone.muscles.push(muscle);
  if (hasBoneAt({ bone, right: !right, start: right, outerLoad: false })) {
    shifts[0] = "p1";
  } else if (hasBoneAt({ bone, right, start: right, innerLoad: false })) {
    shifts[0] = "n1";
  } else if (hasBoneAt({ bone, right: !right, start: right, outerLoad: true })) {
    shifts[0] = useGrid ? "p1" : "n1";
  } else if (hasBoneAt({ bone, right, start: right, innerLoad: true })) {
    shifts[0] = "n3";
  }
  if (hasBoneAt({ bone, right: !right, start: !right, outerLoad: true })) {
    shifts[1] = "p2";
  } else if (hasBoneAt({ bone, right: !right, start: right, outerLoad: false })) {
    shifts[1] = "p1";
  } else if (hasBoneAt({ bone, right: !right, start: !right, outerLoad: false })) {
    shifts[1] = "p1";
  } else if (hasBoneAt({ bone, right, start: !right, innerLoad: false })) {
    shifts[1] = "n1";
  } else if (hasBoneAt({ bone, right, start: !right, innerLoad: true })) {
    shifts[1] = "n2";
  }
  return { muscle, shifts };
}
function shiftMuscle(muscle, shifts, shiftOptions) {
  const mVector = MuscleToVector(muscle, _vectorA).normalize();
  muscle.points[0] = _vectorB.fromArray(muscle.points[0]).addScaledVector(mVector, -shiftOptions[shifts[0]]).toArray();
  muscle.points[1] = _vectorB.fromArray(muscle.points[1]).addScaledVector(mVector, shiftOptions[shifts[1]]).toArray();
}
function setSkipCores(muscle) {
  if (isSingleLoad(muscle.bone)) {
    muscle.skipCores = false;
    return;
  }
  muscle.skipCores = !isInnerMuscle(muscle);
}
function isSingleLoad(bone) {
  return bone.loads.filter((load) => load).length === 1;
}
function isInnerMuscle(muscle) {
  if (hasBoneAt({
    bone: muscle.bone,
    right: muscle.side === BoneSide.Right,
    start: true,
    innerLoad: true
  }))
    return true;
  if (hasBoneAt({
    bone: muscle.bone,
    right: muscle.side === BoneSide.Right,
    start: false,
    innerLoad: true
  }))
    return true;
  return false;
}
function setMuscleCloseEnds(muscle, shifts, useGrid) {
  muscle.canCloseEnds[0] = shiftWithCloseEnds.includes(shifts[0]);
  muscle.canCloseEnds[1] = shiftWithCloseEnds.includes(shifts[1]);
  if (!useGrid)
    return;
  if (muscle.bone.joints[0].bones.length > 1) {
    muscle.canCloseEnds[muscle.side === BoneSide.Right ? 0 : 1] = false;
  }
  if (muscle.bone.joints[1].bones.length > 1) {
    muscle.canCloseEnds[muscle.side === BoneSide.Right ? 1 : 0] = false;
  }
}
function setMuscleLocks(muscle, shifts) {
  if (muscle.bone.joints.every((j) => j.bones.length === 1)) {
    muscle.lockEnds[0] = muscle.side === BoneSide.Right;
    muscle.lockEnds[1] = muscle.side !== BoneSide.Right;
    return;
  }
  muscle.lockEnds[0] = muscleLocksDict[shifts[0]];
  muscle.lockEnds[1] = muscleLocksDict[shifts[1]];
  if (!muscle.lockEnds[0] && !muscle.lockEnds[1])
    muscle.lockEnds[0] = true;
  if (muscle.lockEnds[0] && muscle.lockEnds[1])
    muscle.lockEnds[1] = false;
}
function setMuscleTravelDist(muscle, travelDist, maxCoreToCoreDist) {
  muscle.corridorDist.end = travelDist;
  muscle.corridorDist.middle = maxCoreToCoreDist;
  muscle.corridorDist.start = travelDist;
  const right = muscle.side === BoneSide.Right;
  const coreAtStart = hasCornerCore({ bone: muscle.bone, right, start: true });
  const coreAtEnd = hasCornerCore({ bone: muscle.bone, right, start: false });
  if (right) {
    if (coreAtStart)
      muscle.corridorDist.start = maxCoreToCoreDist;
    if (coreAtEnd)
      muscle.corridorDist.end = maxCoreToCoreDist;
  } else {
    if (coreAtStart)
      muscle.corridorDist.end = maxCoreToCoreDist;
    if (coreAtEnd)
      muscle.corridorDist.start = maxCoreToCoreDist;
  }
}
function initMusclePoints({ muscle, corridorWidth, right }) {
  const side = BoneToVector(muscle.bone, _vectorA).cross(Vectors.Up).normalize().multiplyScalar(corridorWidth * 0.5);
  if (!right)
    side.negate();
  muscle.points[0] = _vectorB.fromArray(muscle.bone.joints[0].pos).add(side).toArray();
  muscle.points[1] = _vectorB.fromArray(muscle.bone.joints[1].pos).add(side).toArray();
  if (muscle.flip)
    muscle.points.reverse();
}
function createMuscle(bone, side) {
  const muscle = {
    bone,
    side,
    id: uuidV4(),
    points: [[0, 0, 0], [0, 0, 0]],
    flip: side === BoneSide.Left,
    lockEnds: [false, false],
    canCloseEnds: [false, false],
    skipCores: false,
    gFUnits: [],
    fireCores: 0,
    liftCores: 0,
    stairCores: 0,
    corridorDist: { start: 0, middle: 0, end: 0 },
    realLength: 0
  };
  return muscle;
}
export {
  AddMusclesToBone
};
//# sourceMappingURL=index49.js.map
